/**
 * A router for the workspace, detached from the one the app is already using.
 *
 * THE PROBLEM. Workspace tabs mount whole surfaces — the Keyword surface, the
 * Investigator page, the Cogni page — and those call `useNavigate`,
 * `useLocation` and `useSearchParams`. They need a router. But they float over
 * a page that has its own, and letting them share it means the workspace
 * rewrites the address bar of the page underneath and its Back button walks
 * the workspace's searches instead of the user's history.
 *
 * The obvious fix, a nested `<MemoryRouter>`, is illegal: React Router's
 * `Router` begins with `useInRouterContext() && invariant(false)` — "You
 * cannot render a <Router> inside another <Router>." It throws, the app-wide
 * error boundary catches it, and the whole page becomes "Something went
 * wrong". Production minification had dropped the invariant's message, so the
 * console showed a bare `Error` with an empty `message` — which is why this
 * cost a staging outage and a revert to find.
 *
 * THE FIX. `useInRouterContext()` is just `useContext(LocationContext) != null`.
 * Blanking that context immediately above the inner router makes the check
 * false, so the nested router mounts and owns everything below it. The route
 * context is blanked with it, or relative paths inside the workspace would
 * resolve against the OUTER page's matched route — `/drug/167041` — instead of
 * the workspace's own entry.
 *
 * The `UNSAFE_` prefix is React Router's marker for "internal, but exported on
 * purpose". Nesting is unsupported; this is the supported way to stop nesting
 * from being nesting. If a future upgrade removes these exports, the failing
 * test is `nestedRouter.test.tsx`, and the alternative is to stop mounting
 * routed surfaces in the workspace at all.
 */
import React from 'react';
import {
  MemoryRouter,
  UNSAFE_LocationContext,
  UNSAFE_RouteContext,
} from 'react-router-dom';

interface DetachedRouterProps {
  /** Starting entry for the private history, e.g. `/?mode=ai&q=...`. */
  initialEntries: string[];
  children: React.ReactNode;
}

// React Router types these contexts as always-populated, because within a
// router they are. Blanking them is the entire point here, so the casts are
// deliberate and local rather than a shrug.
const NO_LOCATION = null as unknown as React.ContextType<typeof UNSAFE_LocationContext>;
const NO_ROUTES = {
  outlet: null, matches: [], isDataRoute: false,
} as unknown as React.ContextType<typeof UNSAFE_RouteContext>;

export const DetachedRouter: React.FC<DetachedRouterProps> = ({
  initialEntries, children,
}) => (
  <UNSAFE_LocationContext.Provider value={NO_LOCATION}>
    <UNSAFE_RouteContext.Provider value={NO_ROUTES}>
      <MemoryRouter initialEntries={initialEntries}>{children}</MemoryRouter>
    </UNSAFE_RouteContext.Provider>
  </UNSAFE_LocationContext.Provider>
);

export default DetachedRouter;
